import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clear, Search } from "@mui/icons-material";
import { IconButton, InputAdornment, Stack } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { DebounceInput } from "@shared/components";

export interface SearchFilterField {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  clearAriaLabel: string;
}

interface SearchFilterProps {
  filterFields: SearchFilterField[];
  onFilterChange?: (filters: Record<string, string>) => void;
  maxWidth?: number | string;
  inputSx?: SxProps<Theme>;
}

const SearchFilter = ({
  filterFields,
  onFilterChange,
  maxWidth = 320,
  inputSx,
}: SearchFilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Initialize filters from URL params
  useEffect(() => {
    const initialFilters: Record<string, string> = {};
    filterFields.forEach((field) => {
      const value = searchParams.get(field.name) || "";
      initialFilters[field.name] = value;
    });
    setFilters(initialFilters);
  }, [filterFields, searchParams]);

  const handleFilterChange = (name: string, value: string) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(name, value);
    } else {
      next.delete(name);
    }
    next.set("page", "1");
    setSearchParams(next, { replace: true });

    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const handleClearFilter = (name: string) => {
    handleFilterChange(name, "");
  };

  return (
    <Stack direction="row" gap="15px" alignItems="flex-end">
      {filterFields.map((field) => {
        const value = filters[field.name] || "";
        return (
          <DebounceInput
            key={field.id}
            placeholder={field.placeholder}
            variant="outlined"
            value={value}
            disableClearable
            debounceTimeout={500}
            handleDebounce={(val) => handleFilterChange(field.name, val)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={field.clearAriaLabel}
                    edge="end"
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleClearFilter(field.name)}
                    sx={{ visibility: value ? "visible" : "hidden" }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              maxWidth,
              height: "40px",
              "& .MuiOutlinedInput-root": {
                fontSize: 15,
                height: "40px",
                padding: "0 10px 0 0",
              },
              "& .MuiOutlinedInput-input": {
                height: "40px",
                padding: "6px 12px",
              },
              ...inputSx,
            }}
          />
        );
      })}
    </Stack>
  );
};

export default SearchFilter;
